import axios, { AxiosError, AxiosResponse, type AxiosInstance } from 'axios';
import AuthService from '~/services/authService';

interface ApiError {
  key: string;
  message: string;
  args: null | any[];
}

interface APIErrorResponseData {
  errors: Array<ApiError>;
  success: false;
}

interface APIResponse {
  data: Record<string, any>;
  metadata?: {
    pagination: {
      pageSize: number;
      pageCount: number;
      resultCount: number;
      currentPage: number;
    };
    sort: 'string';
  };
  success: 'boolean';
}

interface HttpServiceError extends AxiosError {
  response?: AxiosResponse<APIErrorResponseData>;
}
interface HttpServiceResponse extends AxiosResponse {
  data: APIResponse;
}

/**
 * Provides a wrapper around Axios for HTTP requests with built-in authentication, error handling, and interceptors.
 *
 * @remarks
 * - Automatically attaches authentication tokens to requests when required.
 * - Handles token refresh and user disconnection on authentication failures.
 * - Centralizes error handling for common HTTP status codes (400, 401, 403, 422, 500).
 * - Allows initialization with a base URL and customizes request/response interceptors.
 * - Exposes HTTP methods (`get`, `post`, `put`, `delete`, `patch`) bound to the Axios instance.
 *
 * @example
 * ```typescript
 * const httpService = new HttpService();
 * httpService.CallInit('https://api.example.com');
 * const response = await httpService.get('/endpoint');
 * ```
 */
class HttpService {
  private http: AxiosInstance;

  constructor() {
    this.http = axios.create({
      timeout: 60 * 1000, // 60 seconds
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'Access-Control-Allow-Origin': '*',
        withCredentials: true,
      },
    });
  }

  /**
   * Initializes the HTTP service with the specified base URL and sets up request and response interceptors.
   *
   * @param baseURL - The base URL to be used for all HTTP requests.
   *
   * The request interceptor:
   * - Ensures the headers object exists on each request.
   * - Handles authentication by attaching or removing authorization and refresh tokens based on the `requiresAuth` flag.
   * - Disconnects the user if authentication fails.
   *
   * The response interceptor:
   * - Handles successful responses by returning them as-is.
   * - Handles various error status codes (400, 401, 403, 422, 500) with specific logic for each:
   *   - 400: Logs parameter errors and returns a generic error message.
   *   - 401: Attempts to refresh the token and retries the request if successful; otherwise, disconnects the user.
   *   - 403: Disconnects the user.
   *   - 422: Logs business rule validation errors.
   *   - 500: Logs internal server errors.
   * - For unknown status codes, no specific handling is performed.
   *
   * @remarks
   * This method should be called once during application startup to configure the HTTP client.
   */
  CallInit(baseURL: string): void {
    this.http.defaults.baseURL = baseURL;

    this.http.interceptors.request.use(
      (config: any) => {
        const { requiresAuth = true } = config;

        if (requiresAuth === false || config.headers.requiresAuth === false) {
          delete config.headers.authorization;
          delete config.headers['refresh-token'];
        } else {
          const authenticated = AuthService.CallIsAuthenticated();
          if (authenticated === false) AuthService.CallDisconnect();
          else {
            config.headers.authorization = `Bearer ${AuthService.CallGetToken()}`;
            config.headers['refresh-token'] = `Bearer ${AuthService.CallGetRefreshToken()}`;
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    this.http.interceptors.response.use(
      (response: HttpServiceResponse) => {
        /**
         * Handle successful responses.
         * The ADR.12 states that the API returns:
         * - 200 for successful requests
         * - 201 for successful creation requests
         * - 204 for successful deletion requests
         *
         * Also returns two types of body, the first one having a 'success' property
         * and a 'data' property, and the second one (when returning list of items),
         * having the additional 'metadata' property for pagination and sorting.
         *
         * - should whe handle the response body here?
         * - should we return the response as is?
         * - should we transform the response body?
         * - should we show a message to the user?
         * - should we log the response?
         *
         */
        return response;
      },
      async (error: HttpServiceError) => {
        const { response, status } = error;
        if (!response) return Promise.reject(error);

        /**
         * Handle request errors based on status codes.
         */
        if (!response.data || typeof response.data !== 'object') {
          if (status === 404) {
            console.error(
              `🤷 URL_NOT_FOUND:: The URL already exists?`,
              `\nURL: ${error?.config?.url}`,
            );
          } else console.error('⁉️ UNKNOWN_REQUEST_ERROR::', error);

          return {
            data: { success: false },
          };
        }

        // Check if status exists
        if (status === undefined || status === null) {
          console.error('🚨 MISSING_STATUS:', error);
          return Promise.reject(error);
        }

        const { errors, success } = response.data as APIErrorResponseData;

        if (status === 400) {
          /**
           * Handle bad request errors.
           * This typically means that the request parameters are incorrect.
           * // TODO: Add a more specific error handling logic for this case.
           * - what should we do in this case?
           * - this means that the front-end app sent a request with invalid parameters,
           *  could it be that the form validation isn't working correctly?
           *  And if so, should we show a message to the user?
           *  And if so, what message should we show, because, user can be filling
           *  the form correctly or not, so we can't just show a generic message but
           *  we can't assume that the user is doing something wrong.
           * - should we log the error?
           * - should we retry the request?
           *
           * NOTE: At the moment we return the success status from the response data
           *  and the component shows a generic error message.
           */
          const messages: Array<string> | null =
            errors?.map((e) => e.message.replace('.', '')) ?? null;

          if (messages === null || messages.length === 0) {
            console.error(
              `🤷 UNKNOWN_PARAMETERS:: The request params are not valid\nURL: ${error.config?.url}`,
            );
            return {
              data: { success, reason: 'UNKNOWN_PARAMETERS' },
            };
          }

          console.error(
            `🤷 UNKNOWN_PARAMETERS:: The request params are not valid\nURL: ${error.config?.url}\n`,
            messages.join(';\n'),
          );

          return {
            data: { success, reason: 'UNKNOWN_PARAMETERS', keys: errors.map((e) => e.key) },
          };
        }

        if (status === 401) {
          /** Unauthorized, handle accordingly
           * - try to refresh the token
           * - if refresh fails, redirect to login
           */

          const isDownloadRequest =
            error.config?.url?.includes('/download') ||
            error.config?.url?.includes('/statement') ||
            error.config?.responseType === 'blob';

          if (isDownloadRequest) {
            // Para downloads, apenas log de erro sem logout
            console.error(`Download authentication error: ${error.config?.url}`);
            return Promise.reject(error);
          }

          try {
            const refreshResult = await AuthService.CallTokenRefresh();
            if (refreshResult && AuthService.CallIsAuthenticated()) {
              /** Refresh token was successful, retry the request.
               * We need to update the authorization header with the new token.
               */
              if (error.config?.headers) {
                error.config.headers.authorization = `Bearer ${AuthService.CallGetToken()}`;
                return this.http.request(error.config);
              }
            } else {
              /** Token refresh was unsuccessful. */
              AuthService.CallDisconnect();
              return;
            }
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }

        if (status === 403) {
          /** Forbidden, handle accordingly
           * // TODO: Add especially error handling logic for this case.
           * - what should we do in this case?
           * - should we show a message to the user?
           * - should we log the error?
           * - should we redirect the user to login?
           * - should we log out the user?
           *
           * NOTE: At the moment, we just disconnect the user.
           */
          const isFromOrders = error.config?.url?.includes('orders');
          if (isFromOrders) {
            console.error(`Access forbidden to orders: ${error.config?.url}`);
            return Promise.reject(error);
          } else {
            AuthService.CallDisconnect();
            return;
          }
        }

        if (status === 404) {
          const messages: Array<string> | null =
            errors?.map((e) => e.message.replace('.', '')) ?? null;

          if (messages === null || messages.length === 0) {
            console.error(
              `🌵 NOT_FOUND:: No results found for the request`,
              `\nURL: ${error.config?.url}`,
            );
            return {
              data: { success, reason: 'NO_RESULTS_FOUND' },
            };
          } else
            console.error(`🌵 NOT_FOUND:: ${messages.join(';\n')}`, `\nURL: ${error.config?.url}`);

          return {
            data: { success, reason: 'NO_RESULTS_FOUND', keys: errors.map((e) => e.key) },
          };
        }

        if (status === 422) {
          /**
           * Handle business rule errors.
           *
           * // TODO: Add a more specific error handling logic for this case.
           * - what should we do in this case?
           * - should we show a message to the user?
           * - should we log the error?
           *
           * NOTE: At the moment we return the success status from the response data
           *  and the component shows a generic error message.
           */
          const messages: Array<string> | null =
            errors?.map((e) => e.message.replace('.', '')) ?? null;

          if (messages === null || messages.length === 0) {
            console.error(
              `⁉️ UNKNOWN_ERROR:: something went wrong\nURL: ${error.config?.url}`,
              error,
            );

            return {
              data: { success, reason: 'UNKNOWN_ERROR' },
            };
          } else
            console.error(
              `❌ VALIDATION_ERROR:: ${messages.join(';\n')}`,
              `\nURL: ${error.config?.url?.split('/').pop()?.toUpperCase()}`,
            );

          return {
            data: {
              success,
              reason: 'VALIDATION_ERROR',
              keys: errors.map((e) => e.key),
            },
          };
        }

        if (status === 500) {
          /** Internal server error, handle accordingly
           * // TODO: Add especially error handling logic for this case.
           * - what should we do in this case?
           * - should we show a message to the user?
           * - should we log the error?
           * - should we retry the request?
           * - should we redirect the user to an error page?
           * - should we log out the user?
           *
           * NOTE: At the moment, we just log the error and show a generic error message.
           */
          if (
            response.data.errors &&
            Array.isArray(response.data.errors) &&
            response.data.errors.length > 0
          ) {
            console.error(
              '🚨 INTERNAL_SERVER_ERROR:',
              response.data.errors.map((err: any) => err.message).join('\n'),
            );
          } else {
            console.error('🚨 INTERNAL_SERVER_ERROR:', '');
          }
        }

        // For unknown status codes, just return without any handling
        // (no console.error, no rejection)
        return { data: { success, reason: 'INTERNAL_SERVER_ERROR' } };
      },
    );
  }

  get get() {
    return this.http.get.bind(this.http);
  }

  get post() {
    return this.http.post.bind(this.http);
  }

  get put() {
    return this.http.put.bind(this.http);
  }

  get delete() {
    return this.http.delete.bind(this.http);
  }

  get patch() {
    return this.http.patch.bind(this.http);
  }
}

/**
 * An instance of the `HttpService` class used to perform HTTP requests throughout the application.
 *
 * @remarks
 * This service provides methods for making HTTP calls such as GET, POST, PUT, and DELETE.
 * It is intended to be used as a singleton for consistent HTTP communication.
 *
 * @see HttpService
 */
export default new HttpService();
